import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from './usersAPI';

export const loadUsers = createAsyncThunk('users/load', async (_, { rejectWithValue }) => {
  try {
    return await api.fetchUsers();
  } catch (err) {
    // Return serializable error message
    return rejectWithValue(err.message || String(err));
  }
});

export const addUser = createAsyncThunk('users/add', async (payload, { rejectWithValue }) => {
  try {
    return await api.createUser(payload);
  } catch (err) {
    return rejectWithValue(err.message || String(err));
  }
});

export const editUser = createAsyncThunk('users/edit', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await api.updateUser(id, payload);
  } catch (err) {
    return rejectWithValue(err.message || String(err));
  }
});

export const removeUser = createAsyncThunk('users/remove', async (id, { rejectWithValue }) => {
  try {
    await api.deleteUser(id);
    return id;
  } catch (err) {
    return rejectWithValue(err.message || String(err));
  }
});

const usersSlice = createSlice({
  name: 'users',
  initialState: { items: [], status: 'idle', error: null },
  reducers: {
    clearError: (state) => { state.error = null; }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUsers.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(loadUsers.fulfilled, (state, action) => { state.status = 'succeeded'; state.items = action.payload; state.error = null; })
      .addCase(loadUsers.rejected, (state, action) => { 
        state.status = 'failed'; 
        state.error = action.payload || action.error?.message || 'Failed to load users';
      })

      .addCase(addUser.pending, (state) => { state.error = null; })
      .addCase(addUser.fulfilled, (state, action) => { state.items.push(action.payload); state.error = null; })
      .addCase(addUser.rejected, (state, action) => { 
        state.error = action.payload || action.error?.message || 'Failed to add user';
      })

      .addCase(editUser.pending, (state) => { state.error = null; })
      .addCase(editUser.fulfilled, (state, action) => {
        const idx = state.items.findIndex(u => u._id === action.payload._id);
        if (idx !== -1) state.items[idx] = action.payload;
        state.error = null;
      })
      .addCase(editUser.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || 'Failed to update user';
      })

      .addCase(removeUser.pending, (state) => { state.error = null; })
      .addCase(removeUser.fulfilled, (state, action) => {
        state.items = state.items.filter(u => u._id !== action.payload);
        state.error = null;
      })
      .addCase(removeUser.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || 'Failed to delete user';
      });
  }
});

export const { clearError } = usersSlice.actions;
export default usersSlice.reducer;
